import { useCallback } from 'react';

import { getPaymentErrorContent, useAfterPurchaseModalStore } from '@/entities/afterPurchase';
import { usePendingPaymentSession } from '@/entities/paymentSession';
import { usePurchaseConfirmationModalStore } from '@/entities/purchaseConfirmation';

import { API } from '@/shared/api';
import { CONFLICT_STATUS_CODE, FORBIDDEN_STATUS_CODE } from '@/shared/api/errors';
import { useInterfaceLanguage } from '@/shared/i18n';
import { enqueueAppSnackbar } from '@/shared/lib/snackbar';
import { isErrorGuard } from '@/shared/lib/types';
import { AlertType } from '@/shared/types/Alert';

const PAID_TRIAL_OPERATION_NOT_ALLOWED_DETAIL = 'This action is not available during the paid trial.';

const isPaidTrialRefusal = (error: unknown) =>
  isErrorGuard(error, FORBIDDEN_STATUS_CODE) &&
  (error as { data?: { detail?: unknown } }).data?.detail === PAID_TRIAL_OPERATION_NOT_ALLOWED_DETAIL;

type PlanChangeSuccessDescriptions = {
  noPayment: string;
  paid: string;
};

type UsePlanChangeConfirmationArgs = {
  onClosedAfterProcessing: () => void;
};

type OpenPlanChangeConfirmationArgs = {
  confirmButtonText: string;
  errorMessage: string;
  planId: string;
  priceId: string;
  successDescriptions: PlanChangeSuccessDescriptions;
  onConfirmAnalytics: () => void;
  // Runs once the plan change has been paid for, right away or after a pending invoice is paid.
  onPaid?: () => void;
};

export const usePlanChangeConfirmation = ({ onClosedAfterProcessing }: UsePlanChangeConfirmationArgs) => {
  const openConfirmation = usePurchaseConfirmationModalStore((state) => state.open);
  const closeConfirmation = usePurchaseConfirmationModalStore((state) => state.close);
  const { openError } = useAfterPurchaseModalStore();
  const { t } = useInterfaceLanguage();
  const { resolvePaymentResult, showPendingInvoiceIfAny } = usePendingPaymentSession({
    onClosedAfterProcessing,
  });

  return useCallback(
    ({
      confirmButtonText,
      errorMessage,
      planId,
      priceId,
      successDescriptions,
      onConfirmAnalytics,
      onPaid,
    }: OpenPlanChangeConfirmationArgs) => {
      openConfirmation({
        confirmButtonText,
        promoCodeConfig: {
          enabled: true,
          planId,
        },
        fetchPreview: async (promoCode) => {
          try {
            return await API.getPlanInvoicePreview({
              price_id: priceId,
              promotion_code: promoCode ?? undefined,
            });
          } catch (error) {
            if (isPaidTrialRefusal(error)) {
              closeConfirmation();
              enqueueAppSnackbar(t('paidTrial.upgradeOnly'), { variant: AlertType.Info });
            }
            throw error;
          }
        },
        onConfirm: async (invoice, promoCode) => {
          if (await showPendingInvoiceIfAny()) return;

          try {
            const res = await API.updatePlan({
              price_id: priceId,
              promotion_code: promoCode ?? undefined,
            });

            const amountDueToday = invoice?.instant_payment?.amount ?? 0;
            const successDescription = amountDueToday === 0 ? successDescriptions.noPayment : successDescriptions.paid;

            await resolvePaymentResult({
              response: res,
              showInvoiceLink: !!amountDueToday,
              successDescription,
              onPaid,
            });
          } catch (error) {
            if (isPaidTrialRefusal(error)) {
              enqueueAppSnackbar(t('paidTrial.upgradeOnly'), { variant: AlertType.Info });
              return;
            }
            if (isErrorGuard(error, CONFLICT_STATUS_CODE) && (await showPendingInvoiceIfAny())) return;

            console.error(errorMessage, error);
            const errorContent = getPaymentErrorContent(undefined, t);
            openError(errorContent.message, '', errorContent.buttonText);
            throw error;
          }
        },
        onConfirmAnalytics,
      });
    },
    [closeConfirmation, openConfirmation, openError, resolvePaymentResult, showPendingInvoiceIfAny, t],
  );
};
